// Вложения в чате: полоска приложенных файлов в поле ввода, миниатюры и карточки в ленте, просмотр крупно.
import {
  ATTACH_LIMITS,
  type Attachment,
  type AttachmentKind,
  attachmentMeta,
  formatBytes,
  formatDuration,
} from '@autoaff/protocol';
import { toast } from '@autoaff/ui';
import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Download,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Film,
  Image as ImageIcon,
  ImageOff,
  Music,
  Paperclip,
  Play,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type AttachmentRef,
  guessKind,
  heicToJpeg,
  mediaHints,
  mediaSrc,
  type Pending,
  pastedName,
  precheck,
  refOf,
  uploadAttachment,
} from './attach';
import './attachments.css';

function kindIcon(kind: AttachmentKind, format = '', broken = false) {
  if (kind === 'image') return broken ? ImageOff : ImageIcon;
  if (kind === 'video') return Film;
  if (kind === 'audio') return Music;
  if (/CSV|TSV|Excel|таблица/i.test(format)) return FileSpreadsheet;
  if (/PDF|Word|текст|Markdown|JSON|субтитры/i.test(format)) return FileText;
  return FileIcon;
}

// ─── поле ввода: приложенные файлы ─────────────────────────────────────
let seq = 0;

/** Приложенные к сообщению файлы: загрузка сразу после добавления, к отправке — ссылки на загруженное. */
export function useAttachments() {
  const [items, setItems] = useState<Pending[]>([]);
  const cur = useRef<Pending[]>([]);
  const settled = useRef(new Map<string, Promise<void>>());
  const aborts = useRef(new Map<string, () => void>());
  const apply = useCallback((fn: (xs: Pending[]) => Pending[]) => {
    cur.current = fn(cur.current);
    setItems(cur.current);
  }, []);
  const patch = useCallback(
    (key: string, p: Partial<Pending>) => apply((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x))),
    [apply],
  );

  const add = useCallback(
    (files: File[], opts: { pasted?: boolean } = {}) => {
      const room = ATTACH_LIMITS.files - cur.current.length;
      if (files.length > room) toast(`Не больше ${ATTACH_LIMITS.files} файлов в одном сообщении`);
      for (const f of files.slice(0, Math.max(0, room))) {
        const key = `a${++seq}`;
        const name = opts.pasted ? pastedName(f) : f.name || 'файл';
        const kind = guessKind(f);
        const error = precheck({ name, type: f.type, size: f.size });
        apply((xs) => [
          ...xs,
          {
            key,
            name,
            size: f.size,
            kind,
            preview: kind === 'image' && !error ? URL.createObjectURL(f) : null,
            status: error ? 'error' : 'uploading',
            progress: 0,
            ...(error ? { error } : {}),
          },
        ]);
        if (error) continue;
        settled.current.set(
          key,
          (async () => {
            try {
              const file = await heicToJpeg(name === f.name ? f : new File([f], name, { type: f.type }));
              if (!cur.current.some((x) => x.key === key)) return;
              const job = uploadAttachment(file, await mediaHints(file), (progress) =>
                patch(key, { progress }),
              );
              aborts.current.set(key, job.abort);
              const result = await job.promise;
              patch(key, { status: 'ready', progress: 1, result, kind: result.kind });
            } catch (e) {
              patch(key, { status: 'error', error: (e as Error).message });
            } finally {
              aborts.current.delete(key);
            }
          })(),
        );
      }
    },
    [apply, patch],
  );

  const remove = useCallback(
    (key: string) => {
      aborts.current.get(key)?.();
      const x = cur.current.find((i) => i.key === key);
      if (x?.preview) URL.revokeObjectURL(x.preview);
      settled.current.delete(key);
      apply((xs) => xs.filter((i) => i.key !== key));
    },
    [apply],
  );

  /** Дождаться загрузки всех файлов; не загрузился хоть один — ошибка (владелец уберёт его или приложит заново). */
  const ready = useCallback(async (): Promise<AttachmentRef[]> => {
    // Пока ждём, владелец может добавить ещё файлы — ждём и их
    for (let n = 0; n < 20; n++) {
      const waiting = cur.current.filter((i) => i.status === 'uploading');
      if (!waiting.length) break;
      await Promise.all(waiting.map((i) => settled.current.get(i.key) ?? Promise.resolve()));
    }
    const bad = cur.current.find((i) => i.status !== 'ready' || !i.result);
    if (bad)
      throw new Error(`«${bad.name}»: ${bad.error ?? 'не загрузился'} — уберите его или приложите заново`);
    return cur.current.map((i) => refOf(i.result as Attachment));
  }, []);

  /** Забрать всё из поля (отправка); вернуть обратно, если отправить не вышло. */
  const take = useCallback(() => {
    const taken = cur.current;
    apply(() => []);
    return {
      restore: () => apply((xs) => [...taken, ...xs]),
      drop: () => {
        for (const i of taken) {
          if (i.preview) URL.revokeObjectURL(i.preview);
          settled.current.delete(i.key);
        }
      },
    };
  }, [apply]);

  useEffect(
    () => () => {
      for (const a of aborts.current.values()) a();
      for (const i of cur.current) if (i.preview) URL.revokeObjectURL(i.preview);
    },
    [],
  );

  return { items, add, remove, ready, take, busy: items.some((i) => i.status === 'uploading') };
}

export function ComposerAttachments({
  items,
  onRemove,
}: {
  items: Pending[];
  onRemove: (key: string) => void;
}) {
  return (
    <ul className="catt" aria-label="Приложенные файлы">
      {items.map((p) => (
        <PendingTile key={p.key} p={p} onRemove={() => onRemove(p.key)} />
      ))}
    </ul>
  );
}

function PendingTile({ p, onRemove }: { p: Pending; onRemove: () => void }) {
  const [broken, setBroken] = useState(false);
  const pct = Math.round(p.progress * 100);
  const asImage = p.kind === 'image' && p.preview && !broken && p.status !== 'error';
  const Icon = p.status === 'error' ? CircleAlert : kindIcon(p.kind, p.result?.format);
  const line =
    p.status === 'error'
      ? p.error
      : p.status === 'uploading'
        ? `Загружается… ${pct}%`
        : p.result
          ? // В поле ввода место узкое: формат, длительность и размер — без размеров кадра
            [
              p.result.format,
              p.result.durationSec ? formatDuration(p.result.durationSec) : null,
              formatBytes(p.result.bytes),
            ]
              .filter(Boolean)
              .join(' · ')
          : formatBytes(p.size);
  return (
    <li
      className={`catt-item ${p.status}${asImage ? ' is-img' : ''}`}
      title={`${p.name}${line ? ` — ${line}` : ''}`}
    >
      {asImage ? (
        <span className="catt-img">
          <img src={p.preview ?? ''} alt={p.name} onError={() => setBroken(true)} />
          {p.status === 'uploading' && (
            <span className="catt-prog" role="status" aria-label={`Загружается: ${pct}%`}>
              <b className="tnum">{pct}%</b>
            </span>
          )}
        </span>
      ) : (
        <span className="catt-file">
          <span className="catt-ico">
            <Icon />
          </span>
          <span className="catt-txt">
            <b>{p.name}</b>
            <span
              className={p.status === 'error' ? 'err' : 'tnum'}
              role={p.status === 'error' ? 'alert' : undefined}
            >
              {line}
            </span>
          </span>
        </span>
      )}
      <button type="button" className="catt-x" aria-label={`Убрать «${p.name}»`} onClick={onRemove}>
        <X />
      </button>
    </li>
  );
}

/** Файлы, перетащенные на разговор: подсветка всей области чата и приём по отпусканию. */
export function useDropFiles(
  zoneOf: () => HTMLElement | null | undefined,
  onFiles: (files: File[]) => void,
): React.ReactNode {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const cb = useRef(onFiles);
  cb.current = onFiles;
  // biome-ignore lint/correctness/useExhaustiveDependencies: зона — родитель поля ввода, живёт вместе с ним
  useEffect(() => {
    const zone = zoneOf();
    if (!zone) return;
    let depth = 0;
    const files = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes('Files');
    const enter = (e: DragEvent) => {
      if (!files(e)) return;
      e.preventDefault();
      depth++;
      setRect(zone.getBoundingClientRect());
    };
    const over = (e: DragEvent) => {
      if (!files(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const leave = (e: DragEvent) => {
      if (!files(e)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) setRect(null);
    };
    const drop = (e: DragEvent) => {
      if (!files(e)) return;
      e.preventDefault();
      depth = 0;
      setRect(null);
      const list = [...(e.dataTransfer?.files ?? [])];
      if (list.length) cb.current(list);
    };
    zone.addEventListener('dragenter', enter);
    zone.addEventListener('dragover', over);
    zone.addEventListener('dragleave', leave);
    zone.addEventListener('drop', drop);
    return () => {
      zone.removeEventListener('dragenter', enter);
      zone.removeEventListener('dragover', over);
      zone.removeEventListener('dragleave', leave);
      zone.removeEventListener('drop', drop);
    };
  }, []);
  return rect
    ? createPortal(
        <div
          className="drop-veil"
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
          aria-hidden
        >
          <span>
            <Paperclip />
            Отпустите — файлы приложатся к сообщению
          </span>
        </div>,
        document.body,
      )
    : null;
}

// ─── лента: вложения сообщения владельца ───────────────────────────────
const shown = (a: Attachment) => a.kind === 'image' && !!(a.thumb || a.view);

/** Размер одиночной картинки в ленте: целиком, не больше 280×320, без увеличения маленьких. */
function singleSize(a: Attachment): { width: number; height: number } {
  const w = a.viewWidth ?? a.width ?? 280;
  const h = a.viewHeight ?? a.height ?? 200;
  const s = Math.min(1, 280 / w, 320 / h);
  return { width: Math.max(48, Math.round(w * s)), height: Math.max(48, Math.round(h * s)) };
}

export function MessageAttachments({ items }: { items: Attachment[] }) {
  const [open, setOpen] = useState<Attachment | null>(null);
  const images = items.filter(shown);
  const files = items.filter((a) => !shown(a));
  const viewable = items.filter((a) => shown(a) || a.kind === 'video');
  return (
    <div className="matt">
      {images.length > 0 && (
        <div className={`matt-imgs${images.length === 1 ? ' one' : ''}`}>
          {images.map((a) => (
            <button
              key={a.path}
              type="button"
              className="matt-img"
              style={images.length === 1 ? singleSize(a) : undefined}
              onClick={() => setOpen(a)}
              aria-label={`Открыть картинку «${a.name}»`}
              title={a.name}
            >
              <img src={mediaSrc(a.thumb ?? a.view ?? a.path)} alt="" loading="lazy" decoding="async" />
            </button>
          ))}
        </div>
      )}
      {files.map((a) => {
        const Icon = kindIcon(a.kind, a.format, a.kind === 'image');
        const body = (
          <>
            <span className="matt-ico">
              <Icon />
              {a.kind === 'video' && (
                <span className="matt-play">
                  <Play />
                </span>
              )}
            </span>
            <span className="catt-txt">
              <b>{a.name}</b>
              <span className="tnum">{attachmentMeta(a)}</span>
            </span>
          </>
        );
        return a.kind === 'video' ? (
          <button
            key={a.path}
            type="button"
            className="matt-file"
            onClick={() => setOpen(a)}
            aria-label={`Посмотреть видео «${a.name}»`}
          >
            {body}
          </button>
        ) : (
          <a
            key={a.path}
            className="matt-file"
            href={mediaSrc(a.path, a.name)}
            download={a.name}
            aria-label={`Скачать «${a.name}»`}
          >
            {body}
          </a>
        );
      })}
      {open && <Viewer items={viewable} start={viewable.indexOf(open)} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ─── просмотр крупно ───────────────────────────────────────────────────
export function Viewer({
  items,
  start,
  onClose,
}: {
  items: Attachment[];
  start: number;
  onClose: () => void;
}) {
  const [i, setI] = useState(Math.max(0, start));
  const [failed, setFailed] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const a = items[i];
  const many = items.length > 1;
  const go = useCallback(
    (d: number) => {
      setFailed(false);
      setI((x) => (x + d + items.length) % items.length);
    },
    [items.length],
  );
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    // Раньше обработчиков панели чата: Esc закрывает просмотр, а не чат
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      } else if (many && e.key === 'ArrowRight') go(1);
      else if (many && e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prev;
      before?.focus?.({ preventScroll: true });
    };
  }, [onClose, go, many]);
  if (!a) return null;
  return createPortal(
    // biome-ignore lint/a11y/useKeyWithClickEvents: закрыть кликом по фону; с клавиатуры — Esc и кнопка «Закрыть»
    <div
      className="lbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Просмотр: ${a.name}`}
      onClick={(e) => {
        // Клик по полю вокруг картинки (не по ней самой и не по кнопкам) закрывает просмотр
        const el = e.target as HTMLElement;
        if (el === e.currentTarget || el.classList.contains('lbox-stage')) onClose();
      }}
    >
      <header className="lbox-head">
        <div className="lbox-title">
          <b>{a.name}</b>
          <span className="tnum">
            {attachmentMeta(a)}
            {many ? ` · ${i + 1} из ${items.length}` : ''}
          </span>
        </div>
        <a
          className="btn btn-sm btn-ghost"
          href={mediaSrc(a.path, a.name)}
          download={a.name}
          aria-label="Скачать оригинал"
          title="Скачать оригинал"
        >
          <Download />
          <span className="hide-m">Скачать</span>
        </a>
        <button
          type="button"
          className="icon-btn"
          aria-label="Закрыть просмотр"
          title="Закрыть"
          onClick={onClose}
          ref={closeRef}
        >
          <X />
        </button>
      </header>
      <div className="lbox-stage">
        {a.kind === 'video' ? (
          failed ? (
            <p className="lbox-note">
              Браузер не показывает этот формат видео — скачайте файл, чтобы посмотреть.
            </p>
          ) : (
            // biome-ignore lint/a11y/useMediaCaption: видео владельца, субтитров у него нет
            <video
              key={a.path}
              src={mediaSrc(a.path)}
              controls
              autoPlay
              playsInline
              onError={() => setFailed(true)}
            />
          )
        ) : (
          <img key={a.path} src={mediaSrc(a.view ?? a.path)} alt={a.name} />
        )}
      </div>
      {many && (
        <>
          <button
            type="button"
            className="lbox-nav prev"
            aria-label="Предыдущее вложение"
            onClick={() => go(-1)}
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            className="lbox-nav next"
            aria-label="Следующее вложение"
            onClick={() => go(1)}
          >
            <ChevronRight />
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
